import { FC, useCallback, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { ImageComponent } from '@/components/Image';
import { groupMember } from '@/helpers/user';
import { useDevice } from '@/hooks/device';
import { groups } from '@/modules/vip/helpers';
import { useCommonStore } from '@/store';

import { ScAnimatedLogo } from './styled';

export const Logo: FC = () => {
  const { logoMin: LogoMin, logo: LogoDesk } = useTheme();
  const { isAuthenticated, brandAssets, user, pendingUser } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      brandAssets: state.brandAssets,
      user: state.user,
      pendingUser: state.pendingUser,
    }))
  );

  const { isMobileDevice } = useDevice();

  const isLogoMin = useMemo(
    () => (isAuthenticated && !pendingUser) || isMobileDevice,
    [isAuthenticated, pendingUser, isMobileDevice]
  );

  const isVip = useMemo(
    () => groups.some((group) => groupMember(group)(user)) && !pendingUser,
    [user, pendingUser]
  );

  const defaultLogo = isLogoMin ? <LogoMin /> : <LogoDesk />;

  const getPromoLogo = useCallback(() => {
    if (!brandAssets) return;

    const logoFromCms = isLogoMin
      ? brandAssets?.logoMobile?.url
      : brandAssets?.logoDesktop?.url;

    if (!logoFromCms) return;

    return brandAssets?.animatedLogo ? (
      <ScAnimatedLogo>
        <object data={logoFromCms} height={32} aria-label="Level Up" />
      </ScAnimatedLogo>
    ) : (
      <ImageComponent
        src={logoFromCms}
        width={isLogoMin ? 45 : 127}
        height={32}
        alt="Level Up"
      />
    );
  }, [brandAssets, isLogoMin]);

  const getVipLogo = useCallback(() => {
    if (!brandAssets || !brandAssets.vipAnimatedLogo || !isVip) return;

    return (
      <ScAnimatedLogo>
        <object
          data={brandAssets.vipAnimatedLogo.url}
          height={32}
          aria-label="Level Up"
        />
      </ScAnimatedLogo>
    );
  }, [brandAssets, isVip]);

  return getPromoLogo() || getVipLogo() || defaultLogo;
};
